import { themedConfirm } from '../../lib/themed-dialogs';
// Mail Proxy Module UI
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  RefreshButton,
  Select,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import { CheckCircle2, Key, Plus, Search, Server, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// ── Types ─────────────────────────────────────────────────────────────────────

interface MailProxyProfile {
  id: number;
  name: string;
  host: string;
  port: number;
  user?: string;
  from: string;
  tls: boolean;
  type: 'smtp' | 'stalwart';
  max_size?: number;
  allowed_domains?: string[];
  description?: string;
  default?: boolean;
  status?: 'connected' | 'disconnected' | 'unknown';
  message?: string;
  stalwart_config?: { admin_url: string; admin_user: string; enabled: boolean };
  allowed_froms?: string[];
}

interface MailProxyListResult {
  ok: boolean;
  profiles: MailProxyProfile[];
  total: number;
}

interface AclRule {
  id: number;
  smtp_profile_id: number;
  api_key_hash?: string;
  scope?: string;
  allowed_from?: string;
  allowed_domain?: string;
  max_size?: number;
  daily_limit?: number;
  enabled: boolean;
  created_at: string;
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status?: MailProxyProfile['status'] }) {
  const { t } = useTranslation();
  const s = status || 'unknown';
  const statusMap = {
    connected: {
      cls: 'text-emerald-400 bg-emerald-400/10 border border-emerald-500/20',
      label: t('mailProxy.statusConnected'),
    },
    disconnected: {
      cls: 'text-red-400 bg-red-400/10 border border-red-500/20',
      label: t('mailProxy.statusDisconnected'),
    },
    unknown: {
      cls: 'text-gray-500 bg-gray-800 border border-gray-700',
      label: t('mailProxy.statusUnknown'),
    },
  };
  const m = statusMap[s] || statusMap.unknown;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-lg font-medium ${m.cls}`}>{m.label}</span>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function MailProxyPage() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title='Mail Proxy'
        description={t('mailProxy.pageDescription')}
        icon={<Server size={24} className='text-blue-400' />}
      />

      <Tabs defaultTab='profiles'>
        <TabList>
          <Tab id='profiles' icon={<Server size={14} />}>{t('mailProxy.tabProfiles')}</Tab>
          <Tab id='acl' icon={<Key size={14} />}>ACL</Tab>
        </TabList>

        <TabPanel id='profiles'>
          <ProfilesTab />
        </TabPanel>
        <TabPanel id='acl'>
          <AclTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}

// ── ACL Tab ───────────────────────────────────────────────────────────────────

interface AclFilters {
  search: string;
  allowedDomain: string;
  allowedFrom: string;
  enabled: 'all' | 'yes' | 'no';
}

function AclTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [showNewAcl, setShowNewAcl] = useState(false);
  const [filters, setFilters] = useState<AclFilters>({
    search: '',
    allowedDomain: '',
    allowedFrom: '',
    enabled: 'all',
  });

  const { data: profilesData } = useQuery<MailProxyListResult>({
    queryKey: ['mail-proxy-profiles'],
    queryFn: () => api.mcp<MailProxyListResult>('mail-proxy.list', {}),
  });

  const { data: aclData, isLoading: aclLoading, refetch: refetchAcl } = useQuery<{
    acl: AclRule[];
    total: number;
  }>({
    queryKey: ['mail-proxy-acl', selectedProfile, filters],
    queryFn: async () => {
      const resp = await api.mcp<{ acl: AclRule[]; total: number }>('mail-proxy.acl', {
        action: 'list',
        ...(selectedProfile ? { profileId: Number(selectedProfile) } : {}),
      });
      // Filtrovat na klientovi podle aktivních filtrů
      let filtered = resp.acl ?? [];
      if (filters.search) {
        const s = filters.search.toLowerCase();
        filtered = filtered.filter(
          (r) =>
            (r.allowed_from && r.allowed_from.toLowerCase().includes(s)) ||
            (r.allowed_domain && r.allowed_domain.toLowerCase().includes(s)) ||
            (r.scope && r.scope.toLowerCase().includes(s)) ||
            (r.api_key_hash && r.api_key_hash.toLowerCase().includes(s)),
        );
      }
      if (filters.allowedDomain) {
        const d = filters.allowedDomain.toLowerCase();
        filtered = filtered.filter((r) => r.allowed_domain?.toLowerCase().includes(d));
      }
      if (filters.allowedFrom) {
        const f = filters.allowedFrom.toLowerCase();
        filtered = filtered.filter((r) => r.allowed_from?.toLowerCase().includes(f));
      }
      if (filters.enabled !== 'all') {
        filtered = filtered.filter((r) => filters.enabled === 'yes' ? r.enabled : !r.enabled);
      }
      return { acl: filtered, total: filtered.length };
    },
  });

  const deleteAclMut = useMutation({
    mutationFn: (id: number) => api.mcp('mail-proxy.acl', { action: 'delete', id }),
    onSuccess: () => {
      toast.success(t('mailProxy.ruleDeleted'));
      qc.invalidateQueries({ queryKey: ['mail-proxy-acl'] });
      qc.invalidateQueries({ queryKey: ['mail-proxy-profiles'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const profiles = profilesData?.profiles ?? [];
  const aclRules = aclData?.acl ?? [];

  // Spočítat statistiky
  const stats = {
    total: aclRules.length,
    enabled: aclRules.filter((r) => r.enabled).length,
    disabled: aclRules.filter((r) => !r.enabled).length,
  };

  return (
    <div className='space-y-5'>
      {/* Card s filtry a statistikami */}
      <Card>
        <CardHeader>
          <CardTitle icon={Key}>{t('mailProxy.aclRulesTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Statistiky */}
          <div className='grid grid-cols-3 gap-4 mb-5'>
            <div className='bg-gray-800/50 rounded-xl p-3 border border-gray-700'>
              <div className='text-xs text-gray-500'>{t('mailProxy.statTotalRules')}</div>
              <div className='text-xl font-semibold text-white'>{stats.total}</div>
            </div>
            <div className='bg-emerald-900/20 rounded-xl p-3 border border-emerald-500/20'>
              <div className='text-xs text-emerald-400/80'>{t('mailProxy.statEnabledRules')}</div>
              <div className='text-xl font-semibold text-emerald-400'>{stats.enabled}</div>
            </div>
            <div className='bg-gray-800/50 rounded-xl p-3 border border-gray-700'>
              <div className='text-xs text-gray-500'>{t('mailProxy.statDisabledRules')}</div>
              <div className='text-xl font-semibold text-gray-400'>{stats.disabled}</div>
            </div>
          </div>

          {/* Filtrovací řádka */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4'>
            <div className='relative'>
              <Search
                className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-500'
                size={14}
              />
              <input
                type='text'
                placeholder={t('mailProxy.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                className='w-full bg-gray-800 border border-gray-700 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
              />
            </div>
            <Select
              className='w-full'
              value={filters.enabled}
              onChange={(e) =>
                setFilters((f) => ({ ...f, enabled: e.target.value as AclFilters['enabled'] }))}
            >
              <option value='all'>{t('mailProxy.allStatesOption')}</option>
              <option value='yes'>{t('mailProxy.onlyEnabledOption')}</option>
              <option value='no'>{t('mailProxy.onlyDisabledOption')}</option>
            </Select>
            <input
              type='text'
              placeholder={t('mailProxy.filterDomainPlaceholder')}
              value={filters.allowedDomain}
              onChange={(e) => setFilters((f) => ({ ...f, allowedDomain: e.target.value }))}
              className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
            />
            <input
              type='text'
              placeholder={t('mailProxy.filterFromPlaceholder')}
              value={filters.allowedFrom}
              onChange={(e) => setFilters((f) => ({ ...f, allowedFrom: e.target.value }))}
              className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
            />
          </div>

          {/* Profil select a tlačítka */}
          <div className='flex flex-wrap items-center gap-3 pb-2 border-b border-gray-800'>
            <div className='flex items-center gap-2 min-w-[200px]'>
              <label className='text-xs text-gray-500 whitespace-nowrap'>{t('mailProxy.profileLabel')}</label>
              <Select
                className='flex-1'
                value={selectedProfile}
                onChange={(e) => setSelectedProfile(e.target.value)}
              >
                <option value=''>{t('mailProxy.allProfilesOption')}</option>
                {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            </div>
            <div className='flex-1' />
            <div className='flex items-center gap-2'>
              <RefreshButton onRefresh={() => refetchAcl()} />
              <Button
                variant='primary'
                size='sm'
                icon={<Plus size={13} />}
                onClick={() => setShowNewAcl(true)}
              >
                {t('mailProxy.addRule')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ACL tabulka */}
      <Card>
        <CardContent>
          {aclLoading
            ? (
              <div className='text-sm text-gray-500 py-8 text-center'>
                {t('mailProxy.loadingAclRules')}
              </div>
            )
            : aclRules.length === 0
            ? (
              <EmptyState
                icon={<Key size={32} />}
                title={t('mailProxy.noAclRules')}
                description={selectedProfile
                  ? t('mailProxy.addRuleForProfile')
                  : t('mailProxy.selectProfileOrAddRule')}
              />
            )
            : (
              <div className='overflow-x-auto'>
                <table className='w-full text-left text-sm'>
                  <thead>
                    <tr className='border-b border-gray-800 text-gray-400'>
                      <th className='pb-3 pr-4 font-medium'>Allowed from</th>
                      <th className='pb-3 pr-4 font-medium'>Allowed domain</th>
                      <th className='pb-3 pr-4 font-medium'>Scope</th>
                      <th className='pb-3 pr-4 font-medium'>API Key Hash</th>
                      <th className='pb-3 pr-4 font-medium'>Max size</th>
                      <th className='pb-3 pr-4 font-medium'>Daily limit</th>
                      <th className='pb-3 pr-4 font-medium'>Created</th>
                      <th className='pb-3 pr-4 font-medium'>Enabled</th>
                      <th className='pb-3 pl-4 font-medium text-right'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-800'>
                    {aclRules.map((rule) => (
                      <tr key={rule.id} className='hover:bg-gray-800/10 transition-colors group'>
                        <td
                          className='py-3 pr-4 text-gray-300 max-w-[150px] truncate'
                          title={rule.allowed_from || ''}
                        >
                          {rule.allowed_from || <span className='text-gray-600'>-</span>}
                        </td>
                        <td
                          className='py-3 pr-4 text-gray-300 max-w-[150px] truncate'
                          title={rule.allowed_domain || ''}
                        >
                          {rule.allowed_domain || <span className='text-gray-600'>-</span>}
                        </td>
                        <td
                          className='py-3 pr-4 text-gray-300 max-w-[150px] truncate'
                          title={rule.scope || ''}
                        >
                          {rule.scope || <span className='text-gray-600'>-</span>}
                        </td>
                        <td
                          className='py-3 pr-4 text-gray-500 text-xs font-mono max-w-[120px] truncate'
                          title={rule.api_key_hash || ''}
                        >
                          {rule.api_key_hash
                            ? rule.api_key_hash.slice(0, 12) + '...'
                            : <span className='text-gray-600'>-</span>}
                        </td>
                        <td className='py-3 pr-4 text-gray-300'>
                          {rule.max_size ? `${rule.max_size / 1024} MB` : '-'}
                        </td>
                        <td className='py-3 pr-4 text-gray-300'>{rule.daily_limit || '-'}</td>
                        <td className='py-3 pr-4 text-gray-500 text-xs'>
                          {new Date(rule.created_at).toLocaleDateString('cs-CZ')}
                        </td>
                        <td className='py-3 pr-4'>
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-lg font-medium ${
                              rule.enabled
                                ? 'text-emerald-400 bg-emerald-400/10'
                                : 'text-red-400 bg-red-400/10'
                            }`}
                          >
                            {rule.enabled
                              ? t('mailProxy.ruleEnabledBadge')
                              : t('mailProxy.ruleDisabledBadge')}
                          </span>
                        </td>
                        <td className='py-3 pl-4 text-right'>
                          <div className='flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity'>
                            <button
                              onClick={async () => {
                                if (await themedConfirm(t('mailProxy.confirmDeleteAclRule'))) {
                                  deleteAclMut.mutate(rule.id);
                                }
                              }}
                              disabled={deleteAclMut.isPending}
                              className='text-red-400 hover:text-red-300 transition-colors'
                              title={t('mailProxy.delete')}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </CardContent>
      </Card>

      {showNewAcl && (
        <NewAclModal
          profileId={parseInt(selectedProfile)}
          onClose={() => setShowNewAcl(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['mail-proxy-acl'] });
            qc.invalidateQueries({ queryKey: ['mail-proxy-profiles'] });
            setShowNewAcl(false);
            setFilters({ search: '', allowedDomain: '', allowedFrom: '', enabled: 'all' });
          }}
        />
      )}
    </div>
  );
}

function NewAclModal(
  { profileId, onClose, onSaved }: { profileId: number; onClose: () => void; onSaved: () => void },
) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    api_key_hash: '',
    scope: '',
    allowed_from: '',
    allowed_domain: '',
    max_size: '10240',
    daily_limit: '100',
    enabled: true,
  });

  const mut = useMutation({
    mutationFn: () =>
      api.mcp('mail-proxy.acl', {
        action: 'add',
        smtp_profile_id: profileId,
        api_key_hash: form.api_key_hash || undefined,
        scope: form.scope || undefined,
        allowed_from: form.allowed_from || undefined,
        allowed_domain: form.allowed_domain || undefined,
        max_size: Number(form.max_size) || undefined,
        daily_limit: Number(form.daily_limit) || undefined,
        enabled: form.enabled,
      }),
    onSuccess: () => {
      toast.success(t('mailProxy.aclRuleCreated'));
      onSaved();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg shadow-2xl'>
        <div className='px-5 py-4 border-b border-gray-800/60 flex items-center justify-between'>
          <h2 className='text-sm font-semibold text-white'>{t('mailProxy.newAclRuleTitle')}</h2>
          <button onClick={onClose} className='text-gray-500 hover:text-white transition-colors'>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <path d='M18 6L6 18M6 6l12 12' />
            </svg>
          </button>
        </div>
        <div className='p-5 space-y-4'>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Allowed from *</label>
            <input
              value={form.allowed_from}
              onChange={(e) => setForm((f) => ({ ...f, allowed_from: e.target.value }))}
              placeholder={t('mailProxy.exampleEmailPlaceholder')}
              className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>Allowed domain *</label>
            <input
              value={form.allowed_domain}
              onChange={(e) => setForm((f) => ({ ...f, allowed_domain: e.target.value }))}
              placeholder={t('mailProxy.exampleDomainPlaceholder')}
              className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
            />
          </div>
          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>Max size (KB) *</label>
              <input
                value={form.max_size}
                onChange={(e) => setForm((f) => ({ ...f, max_size: e.target.value }))}
                placeholder='10240'
                className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
              />
            </div>
            <div>
              <label className='block text-xs text-gray-500 mb-1'>Daily limit *</label>
              <input
                value={form.daily_limit}
                onChange={(e) => setForm((f) => ({ ...f, daily_limit: e.target.value }))}
                placeholder='100'
                className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
              />
            </div>
          </div>
          <div className='flex items-center gap-2 mt-2'>
            <input
              type='checkbox'
              id='enabled'
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
              className='rounded border-gray-700 bg-gray-800 text-blue-600 focus:ring-blue-600/30'
            />
            <label htmlFor='enabled' className='text-sm text-gray-300'>
              {t('mailProxy.enabledLabel')}
            </label>
          </div>
        </div>
        <div className='px-5 py-3 border-t border-gray-800/60 bg-gray-900/20 flex items-center justify-end gap-2'>
          <button
            onClick={onClose}
            className='px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-medium transition-colors'
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={!form.allowed_from && !form.allowed_domain || mut.isPending}
            className='px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {mut.isPending ? t('mailProxy.saving') : t('mailProxy.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── New Stalwart Profile Modal ────────────────────────────────────────────────

function NewStalwartProfileModal(
  { onClose, onSaved }: { onClose: () => void; onSaved: () => void },
) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    type: 'smtp' as 'smtp' | 'stalwart',
    host: '',
    port: '587',
    user: '',
    password: '',
    from: '',
    tls: true,
    stalwart_admin_url: '',
    stalwart_admin_user: '',
    stalwart_admin_password: '',
    stalwart_smtp_host: '',
    stalwart_smtp_port: '587',
    allowed_domains: '',
    allowed_froms: '',
    max_size: '10240',
    default: false,
    description: '',
  });
  const mut = useMutation({
    mutationFn: () => {
      if (form.type === 'stalwart') {
        return api.mcp('mail-proxy.create', {
          name: form.name,
          type: form.type,
          host: form.stalwart_smtp_host || form.host,
          port: Number(form.stalwart_smtp_port || form.port),
          user: form.user || undefined,
          password: form.password || undefined,
          from: form.from,
          tls: form.tls,
          stalwart_admin_url: form.stalwart_admin_url || undefined,
          stalwart_admin_user: form.stalwart_admin_user || undefined,
          stalwart_admin_password: form.stalwart_admin_password,
          stalwart_smtp_host: form.stalwart_smtp_host || undefined,
          stalwart_smtp_port: Number(form.stalwart_smtp_port) || undefined,
          allowed_domains: form.allowed_domains
            ? form.allowed_domains.split(',').map((d) => d.trim()).filter(Boolean)
            : undefined,
          allowed_froms: form.allowed_froms
            ? form.allowed_froms.split(',').map((d) => d.trim()).filter(Boolean)
            : undefined,
          max_size: Number(form.max_size) || undefined,
          default: form.default,
          description: form.description || undefined,
        });
      }
      return api.mcp('mail-proxy.create', {
        name: form.name,
        type: form.type,
        host: form.host,
        port: Number(form.port),
        user: form.user || undefined,
        password: form.password || undefined,
        from: form.from,
        tls: form.tls,
        allowed_domains: form.allowed_domains
          ? form.allowed_domains.split(',').map((d) => d.trim()).filter(Boolean)
          : undefined,
        allowed_froms: form.allowed_froms
          ? form.allowed_froms.split(',').map((d) => d.trim()).filter(Boolean)
          : undefined,
        max_size: Number(form.max_size) || undefined,
        default: form.default,
        description: form.description || undefined,
      });
    },
    onSuccess: () => {
      toast.success(t('mailProxy.profileCreated'));
      onSaved();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const isStalwart = form.type === 'stalwart';

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4'>
      <div className='bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto'>
        <div className='px-5 py-4 border-b border-gray-800/60 flex items-center justify-between sticky top-0 bg-gray-900 z-10'>
          <h2 className='text-sm font-semibold text-white'>{t('mailProxy.newProfileTitle')}</h2>
          <button onClick={onClose} className='text-gray-500 hover:text-white transition-colors'>
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
            >
              <path d='M18 6L6 18M6 6l12 12' />
            </svg>
          </button>
        </div>
        <div className='p-5 space-y-4'>
          {/* Type selector */}
          <div>
            <label className='block text-xs text-gray-500 mb-1'>{t('mailProxy.typeLabel')}</label>
            <div className='flex gap-3'>
              <label className='flex items-center gap-2 cursor-pointer'>
                <input
                  type='radio'
                  name='type'
                  value='smtp'
                  checked={form.type === 'smtp'}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, type: e.target.value as 'smtp' | 'stalwart' }))}
                  className='text-blue-600 focus:ring-blue-600/30'
                />
                <span className='text-sm text-gray-300'>SMTP</span>
              </label>
              <label className='flex items-center gap-2 cursor-pointer'>
                <input
                  type='radio'
                  name='type'
                  value='stalwart'
                  checked={form.type === 'stalwart'}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, type: e.target.value as 'smtp' | 'stalwart' }))}
                  className='text-blue-600 focus:ring-blue-600/30'
                />
                <span className='text-sm text-gray-300'>Stalwart</span>
              </label>
            </div>
          </div>

          {/* Common fields */}
          <div>
            <label className='block text-xs text-gray-500 mb-1'>{t('mailProxy.nameLabel')}</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={t('mailProxy.namePlaceholder')}
              className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>From e-mail *</label>
            <input
              value={form.from}
              onChange={(e) => setForm((f) => ({ ...f, from: e.target.value }))}
              placeholder={t('mailProxy.emailPlaceholder')}
              className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
            />
          </div>
          <div>
            <label className='block text-xs text-gray-500 mb-1'>
              {t('mailProxy.descriptionLabel')}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder={t('mailProxy.optionalDescriptionPlaceholder')}
              rows={2}
              className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 resize-none'
            />
          </div>

          {/* SMTP fields */}
          {!isStalwart && (
            <>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>SMTP host *</label>
                <input
                  value={form.host}
                  onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
                  placeholder='smtp.gmail.com'
                  className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                />
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='block text-xs text-gray-500 mb-1'>Port *</label>
                  <input
                    value={form.port}
                    onChange={(e) => setForm((f) => ({ ...f, port: e.target.value }))}
                    placeholder='587'
                    className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                  />
                </div>
                <div className='flex items-center gap-2 mt-6'>
                  <input
                    type='checkbox'
                    id='tls'
                    checked={form.tls}
                    onChange={(e) => setForm((f) => ({ ...f, tls: e.target.checked }))}
                    className='rounded border-gray-700 bg-gray-800 text-blue-600 focus:ring-blue-600/30'
                  />
                  <label htmlFor='tls' className='text-sm text-gray-300'>TLS/SSL</label>
                </div>
              </div>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>
                  {t('mailProxy.usernameLabel')}
                </label>
                <input
                  value={form.user}
                  onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))}
                  placeholder={t('mailProxy.emailPlaceholder')}
                  className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                />
              </div>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>
                  {t('mailProxy.passwordLabel')}
                </label>
                <input
                  type='password'
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder='••••••••'
                  className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                />
              </div>
            </>
          )}

          {/* Stalwart fields */}
          {isStalwart && (
            <>
              <div className='border-t border-gray-800 pt-4'>
                <h3 className='text-xs font-semibold text-gray-400 mb-3'>
                  {t('mailProxy.stalwartAdminConfigTitle')}
                </h3>
                <div>
                  <label className='block text-xs text-gray-500 mb-1'>Admin URL *</label>
                  <input
                    value={form.stalwart_admin_url}
                    onChange={(e) => setForm((f) => ({ ...f, stalwart_admin_url: e.target.value }))}
                    placeholder='https://stalwart.mydomain.com'
                    className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                  />
                </div>
                <div className='grid grid-cols-2 gap-3 mt-3'>
                  <div>
                    <label className='block text-xs text-gray-500 mb-1'>Admin User *</label>
                    <input
                      value={form.stalwart_admin_user}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, stalwart_admin_user: e.target.value }))}
                      placeholder='admin@stalwart.local'
                      className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                    />
                  </div>
                  <div>
                    <label className='block text-xs text-gray-500 mb-1'>Admin Password *</label>
                    <input
                      type='password'
                      value={form.stalwart_admin_password}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, stalwart_admin_password: e.target.value }))}
                      placeholder='••••••••'
                      className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                    />
                  </div>
                </div>
              </div>

              <div className='border-t border-gray-800 pt-4'>
                <h3 className='text-xs font-semibold text-gray-400 mb-3'>Stalwart SMTP Settings</h3>
                <div className='grid grid-cols-2 gap-3'>
                  <div>
                    <label className='block text-xs text-gray-500 mb-1'>SMTP Host *</label>
                    <input
                      value={form.stalwart_smtp_host}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, stalwart_smtp_host: e.target.value }))}
                      placeholder='smtp.stalwart.local'
                      className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                    />
                  </div>
                  <div>
                    <label className='block text-xs text-gray-500 mb-1'>SMTP Port *</label>
                    <input
                      value={form.stalwart_smtp_port}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, stalwart_smtp_port: e.target.value }))}
                      placeholder='587'
                      className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Advanced fields */}
          <div className='border-t border-gray-800 pt-4'>
            <h3 className='text-xs font-semibold text-gray-400 mb-3'>
              {t('mailProxy.advancedSettingsTitle')}
            </h3>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>
                  {t('mailProxy.allowedDomainsLabel')}
                </label>
                <input
                  value={form.allowed_domains}
                  onChange={(e) => setForm((f) => ({ ...f, allowed_domains: e.target.value }))}
                  placeholder={t('mailProxy.allowedDomainsPlaceholder')}
                  className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                />
              </div>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>
                  {t('mailProxy.allowedFromsLabel')}
                </label>
                <input
                  value={form.allowed_froms}
                  onChange={(e) => setForm((f) => ({ ...f, allowed_froms: e.target.value }))}
                  placeholder={t('mailProxy.allowedFromsPlaceholder')}
                  className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-3 mt-3'>
              <div>
                <label className='block text-xs text-gray-500 mb-1'>Max size (KB) *</label>
                <input
                  value={form.max_size}
                  onChange={(e) => setForm((f) => ({ ...f, max_size: e.target.value }))}
                  placeholder='10240'
                  className='w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-blue-500'
                />
              </div>
              <div className='flex items-center gap-2 mt-6'>
                <input
                  type='checkbox'
                  id='default'
                  checked={form.default}
                  onChange={(e) => setForm((f) => ({ ...f, default: e.target.checked }))}
                  className='rounded border-gray-700 bg-gray-800 text-blue-600 focus:ring-blue-600/30'
                />
                <label htmlFor='default' className='text-sm text-gray-300'>
                  {t('mailProxy.defaultProfileLabel')}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className='px-5 py-3 border-t border-gray-800/60 bg-gray-900/20 flex items-center justify-end gap-2'>
          <button
            onClick={onClose}
            className='px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm font-medium transition-colors'
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={!form.name || !form.from || mut.isPending}
            className='px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {mut.isPending ? t('mailProxy.saving') : t('mailProxy.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Profile tab ───────────────────────────────────────────────────────────────

function ProfilesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [fastRefresh, setFastRefresh] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const { data, isLoading, refetch } = useQuery<MailProxyListResult>({
    queryKey: ['mail-proxy-profiles'],
    queryFn: () => api.mcp<MailProxyListResult>('mail-proxy.list', {}),
    refetchInterval: fastRefresh ? 3_000 : 30_000,
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.mcp('mail-proxy.delete', { id }),
    onSuccess: () => {
      toast.success(t('mailProxy.profileDeleted'));
      qc.invalidateQueries({ queryKey: ['mail-proxy-profiles'] });
      // Smazání doběhne na pozadí — po dobu minuty se ptej každé 3 s,
      // ať uživatel vidí, že profil zmizel. Stejný vzor jako supabase.
      setFastRefresh(true);
      setTimeout(() => setFastRefresh(false), 60_000);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const profiles = data?.profiles ?? [];
  const cntConnected = profiles.filter((p: MailProxyProfile) => p.status === 'connected').length;
  const cntDisconnected =
    profiles.filter((p: MailProxyProfile) => p.status === 'disconnected').length;

  return (
    <div className='space-y-5'>
      <StatsGrid>
        <StatCard
          icon={Server}
          label={t('mailProxy.statTotal')}
          value={profiles.length}
          sub={t('mailProxy.statTotalSub')}
          variant='info'
        />
        <StatCard
          icon={CheckCircle2}
          label={t('mailProxy.statConnected')}
          value={cntConnected}
          sub={t('mailProxy.statConnectedSub')}
          variant='success'
        />
        <StatCard
          icon={XCircle}
          label={t('mailProxy.statDisconnected')}
          value={cntDisconnected}
          sub={t('mailProxy.statDisconnectedSub')}
          variant='warning'
        />
      </StatsGrid>

      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle icon={Server}>{t('mailProxy.emailProfilesTitle')}</CardTitle>
            <div className='flex items-center gap-2'>
              <RefreshButton onRefresh={() => refetch()} />
              <Button
                variant='primary'
                size='sm'
                icon={<Plus size={13} />}
                onClick={() => setShowNew(true)}
              >
                {t('mailProxy.newProfile')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading
            ? <div className='text-sm text-gray-500 py-4'>{t('mailProxy.loading')}</div>
            : profiles.length === 0
            ? (
              <EmptyState
                icon={<Server size={32} />}
                title={t('mailProxy.noEmailProfiles')}
                description={t('mailProxy.addEmailProfileHint')}
              />
            )
            : (
              <div className='space-y-2'>
                {profiles.map((profile) => (
                  <div
                    key={profile.id}
                    className='rounded-xl border border-gray-800 overflow-hidden hover:border-gray-700 transition-colors'
                  >
                    <div className='flex items-center gap-4 p-4 hover:bg-gray-800/10'>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2 mb-1'>
                          <span className='text-sm font-semibold text-white'>{profile.name}</span>
                          {profile.type === 'stalwart' && (
                            <span className='text-[10px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded'>
                              stalwart
                            </span>
                          )}
                          {profile.default && (
                            <span className='text-[10px] text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded'>
                              default
                            </span>
                          )}
                          <StatusBadge status={profile.status} />
                        </div>
                        <div className='text-xs text-gray-500 flex items-center gap-3 flex-wrap'>
                          <span>{profile.host}:{profile.port}</span>
                          <span>
                            {profile.user ? t('mailProxy.withAuth') : t('mailProxy.withoutAuth')}
                          </span>
                          <span>{profile.tls ? 'TLS/SSL' : t('mailProxy.noTls')}</span>
                          <span className='truncate max-w-[200px]'>
                            {t('mailProxy.fromSender', { from: profile.from })}
                          </span>
                        </div>
                        {profile.description && (
                          <div className='text-xs text-gray-600 mt-1'>{profile.description}</div>
                        )}
                      </div>
                      <div className='flex items-center gap-1'>
                        <Button
                          variant='ghost'
                          size='icon'
                          icon={<Trash2 size={14} />}
                          title={t('mailProxy.delete')}
                          onClick={async () => {
                            if (await themedConfirm(t('mailProxy.confirmDeleteProfile', { name: profile.name }))) {
                              deleteMut.mutate(profile.id);
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </CardContent>
      </Card>

      {showNew && (
        <NewStalwartProfileModal
          onClose={() => setShowNew(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['mail-proxy-profiles'] });
            setShowNew(false);
          }}
        />
      )}
    </div>
  );
}
